import { Component } from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import classnames from 'classnames'
import queryString from 'query-string'
import Collapse from 'react-collapse'

import TimetoastPropTypes from '@constants/timetoast_prop_types.js'
import Form from '@components/forms/form.jsx'
import Label from '@components/forms/label.jsx'
import Hint from '@components/Hint.tsx'
import CopyableInput from '@components/forms/copyable_input.jsx'
import Link from '@components/Link.tsx'
import HorizontalSpinner from '@components/HorizontalSpinner.tsx'
import UpgradePrompt from '@components/UpgradePrompt.tsx'
import { stripNonNumeric, containsNullOrUndefined } from '@utils/string.ts'
import { stringifyColors, defaultColorScheme } from '@helpers/color_scheme_helpers.js'
import Status from '@constants/request_statuses.ts'
import { DEFAULT, TIMELINE, LIST } from '@constants/timeline_view_modes.js'
import EmbedForm from './embed_form.jsx'

export default class EmbedPaneContent extends Component {
  static propTypes = {
    currentUser: TimetoastPropTypes.currentUser,
    isPublished: PropTypes.bool,
    allowEmbedding: PropTypes.bool,
    timelineId: PropTypes.number,
    host: PropTypes.string,
    embedKey: PropTypes.object,
    embedKeyLoadStatus: PropTypes.string,
    loadEmbedKey: PropTypes.func.isRequired,
    colorScheme: TimetoastPropTypes.colorScheme.isRequired,
    changeEditableColorScheme: PropTypes.func.isRequired,
  }

  static defaultProps = {
    currentUser: null,
    embedKey: null,
    loadEmbedKey: noop,
    colorScheme: defaultColorScheme(),
    changeEditableColorScheme: noop,
  }

  constructor(props) {
    super(props)
    this.state = {
      width: '500',
      height: '400',
      mode: DEFAULT,
      expanded: false,
    }
  }

  componentDidMount() {
    const { timelineId, allowEmbedding, embedKey, embedKeyLoadStatus, loadEmbedKey } = this.props
    // only fetch keys if we don't have any and haven't loaded any before. We can also skip loading them if this isn't allowed to be embedded
    if (allowEmbedding && !embedKey && embedKeyLoadStatus === Status.IDLE) {
      loadEmbedKey(timelineId)
    }
  }

  // Not currently used, but probably handy to keep
  getLegacyEmbedCode() {
    const { timelineId, host } = this.props
    const { width, height } = this.getEmbedSize()
    return `<object width="${width}" height="${height}"><param name="movie" value="https://${host}/flash/TimelineViewer.swf?passedTimelines=${timelineId}" /><param name="passedTimelines" value="${timelineId}" /><param name="allowScriptAccess" value="always"><embed src="http://${host}/flash/TimelineViewer.swf?passedTimelines=${timelineId}" type="application/x-shockwave-flash" passedTimelines="${timelineId}" width="${width}" height="${height}" allowScriptAccess="always" /></object>`
  }

  handleFieldChange = (name, value) => {
    this.setState({
      [name]: value,
    })
  }

  handleColorSchemeChange = colorScheme => {
    const { changeEditableColorScheme } = this.props
    changeEditableColorScheme(colorScheme)
  }

  getEmbedSize() {
    let { width, height } = this.state
    // strip non numerics when setting in state as this is directly used in the embed code
    return {
      width: stripNonNumeric(width),
      height: stripNonNumeric(height),
    }
  }

  handleExpanderClick = event => {
    this.setState({
      expanded: !this.state.expanded,
    })
  }

  getPreviewLinkContent() {
    const url = this.getEmbedURL()
    if (url && url.length > 0) {
      return (
        <Link href={url} target="_blank">
          Preview
        </Link>
      )
    }
    return null
  }

  getEmbedCustomizerContent() {
    const { embedKey, colorScheme } = this.props
    const { width, height, mode } = this.state
    const colorCustomization = embedKey ? embedKey.color_customization : false
    const previewLink = this.getPreviewLinkContent()
    return (
      <div className="p embed-pane__extra">
        <h1 className="heading zeta">Customize your code</h1>
        <EmbedForm
          onFieldChange={this.handleFieldChange}
          onColorSchemeChange={this.handleColorSchemeChange}
          colorScheme={colorScheme}
          width={width}
          height={height}
          mode={mode}
          colorCustomization={colorCustomization}
        />
        {previewLink}
      </div>
    )
  }

  getExpanderContent() {
    const { expanded } = this.state
    const text = expanded ? 'Close' : 'Customize your code'
    const className = classnames('embed-pane__expander', { expanded: expanded }, { collapsed: !expanded })
    return (
      <a className={className} onClick={this.handleExpanderClick}>
        <div className="expander__content">
          <div className="expander__text">{text}</div>
          <div className="expander__arrow arrow"></div>
        </div>
      </a>
    )
  }

  getExpandedContent() {
    const { expanded } = this.state
    return (
      <Collapse isOpened={expanded}>
        <div className="react-collapse-jump-fixer">{this.getEmbedCustomizerContent()}</div>
      </Collapse>
    )
  }

  getEmbedKey() {
    const { embedKey } = this.props
    if (embedKey) {
      return apiKeys[0]
    }
    return null
  }

  getIFrameEmbedCode(url, width, height) {
    const { embedKey } = this.props
    if (embedKey && url) {
      const code = `<iframe src="${url}" width="${width}" height="${height}" frameborder="0" allowfullscreen></iframe>`
      if (!containsNullOrUndefined(code)) {
        return code
      }
    }
    return ''
  }

  getOEmbedURL(url, width, height) {
    const { host } = this.props
    return `https://${host}/oembed?url=${url}&width=${width}&height=${height}&format=json`
  }

  // returns the embed url with token and mode query params
  getEmbedURL() {
    const { host, timelineId, embedKey, colorScheme } = this.props
    const { mode } = this.state

    if (embedKey) {
      const { key, active } = embedKey
      const colorCustomization = embedKey ? embedKey.color_customization : false

      const query = {
        token: key,
      }
      if (mode && mode !== DEFAULT) {
        query.mode = mode
      }
      if (colorCustomization) {
        query.colors = stringifyColors(colorScheme)
      }
      const url = queryString.stringifyUrl({
        url: `https://${host}/embed/${timelineId}`,
        query,
      })
      if (!containsNullOrUndefined(url)) {
        return url
      }
    }
    return null
  }

  getInactiveKeyMessageContent() {
    const { embedKey } = this.props
    return embedKey && embedKey.active ? null : <p className="tip">Note: This embed code is not currently active.</p>
  }

  getEmbedCodesContent() {
    const { embedKey, embedKeyLoadStatus } = this.props
    if (embedKey) {
      const url = this.getEmbedURL()
      const { width, height } = this.getEmbedSize()

      const iFrameEmbedCode = this.getIFrameEmbedCode(url, width, height)
      const oEmbedURL = this.getOEmbedURL(url, width, height)
      const inactiveKeyMessage = this.getInactiveKeyMessageContent()
      // Note: Don't autofocus the copyable input as this is displayed in popover layer that is absolutely positioned at top of page causing scroll position to jump to top
      return (
        <div className="p">
          {inactiveKeyMessage}
          <Form
            className="form form--limited-inputs"
            onSubmit={e => {
              e.preventDefault()
            }}
          >
            <Label className="label">Iframe embed</Label>
            <CopyableInput
              inputClassName="input qa-iframe-embed-code-input"
              buttonClassName="btn btn--primary"
              value={iFrameEmbedCode}
              autoFocus={false}
              readOnly={true}
            />
          </Form>

          <Form
            className="form form--limited-inputs"
            onSubmit={e => {
              e.preventDefault()
            }}
          >
            <Label className="label">
              oEmbed url{' '}
              <Hint hint="For oEmbed consumers. E.g. Wordpress etc. Note: This is currently an experimental feature." />
            </Label>
            <CopyableInput
              inputClassName="input qa-oembed-embed-url-input"
              buttonClassName="btn btn--primary"
              value={oEmbedURL}
              autoFocus={false}
              readOnly={true}
            />
          </Form>
        </div>
      )
    } else {
      switch (embedKeyLoadStatus) {
        case Status.IDLE:
        case Status.LOADING:
          return (
            <div className="p">
              <HorizontalSpinner size="large" />
            </div>
          )
          break
        case Status.SUCCEEDED:
          return <p>Error: No token</p>
          break
        case Status.FAILED:
          return <p>Error</p>
          break
        default:
          return null
      }
    }
  }

  getEmbeddingEnabledContent() {
    if (this.isEmbeddable()) {
      const extra = this.getExpandedContent()
      const expander = this.getExpanderContent()
      const embedCodesContent = this.getEmbedCodesContent()
      return (
        <div>
          {embedCodesContent}
          {expander}
          {extra}
        </div>
      )
    }
    return null
  }

  getEmbeddingDisabledContent() {
    const { currentUser, allowEmbedding, isPublished } = this.props
    let content = null
    if (currentUser) {
      if (allowEmbedding && !isPublished) {
        content = <div className="notice notice--info p">This timeline must be published to be embeddable.</div>
      } else {
        content = <UpgradePrompt title="Upgrade to a premium account to enable embedding." ctaText="Upgrade Now" />
      }
    } else {
      content = (
        <UpgradePrompt
          title="Embedding timelines is exclusive to signed-in users with a premium account."
          ctaText="Sign Up"
          ctaPath="/plans"
        />
      )
    }
    return <div className="p">{content}</div>
  }

  isEmbeddable() {
    const { allowEmbedding, isPublished, currentUser } = this.props
    return currentUser && isPublished && allowEmbedding
  }

  getContent() {
    return this.isEmbeddable() ? this.getEmbeddingEnabledContent() : this.getEmbeddingDisabledContent()
  }

  render() {
    return <div className="embed-pane">{this.getContent()}</div>
  }
}
